import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API_CONFIG } from '../../config/api.config';
import { standardFieldLabelSx } from '../../components/common/formLabelStyles';
import useAPICall from '../../hooks/useAPICall';
import useAuth from '../../hooks/useAuth';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import ConfirmDeleteDialog from '../../components/common/ConfirmDeleteDialog';
import {
  DRUG_MODEL_SEED,
  HCP_MODEL_SEED,
} from './configurationSeeds';

const MATCH_ACTIONS = ['Create potential matches', 'Automatically merge'];
const COMPARATORS = ['exact', 'fuzzy'];
const OPERATORS = ['and', 'and not', 'or', 'or not'];
const STRATEGY_TYPES = ['Source Based', 'Aggregation', 'OldValue', 'Recent', 'MaxValue', 'MinValue'];
const SOURCE_OPTIONS = ['Onekey', 'Xpt', 'ConcertAI', 'Enova'];
const BUSINESS_COMPUTATION_KEYS = ['primaryAddress', 'primarySpecialty'];
const HCP_BUSINESS_ATTRIBUTE_OPTIONS = [
  'MDM Id',
  'Onekey Individual Id',
  'Source',
  'Full Name',
  'First Name',
  'Middle Name',
  'Last Name',
  'Suffix',
  'Gender',
  'Birth Year',
  'Address Line 1',
  'Address Line 2',
  'City',
  'State',
  'Zip',
  'Primary Profession',
  'Primary Specialty',
  'Full Profile',
  'NPI Id',
  'Status',
  'Created Date',
  'Updated Date',
  'MDM Created Time',
  'MDM Updated Time',
  'AMA PDRP',
  'Is Eligible',
  'AMA Do Not Call',
  'PMC Cust Id',
];

const DRUG_BUSINESS_ATTRIBUTE_OPTIONS = [
  "Drug Id",
  "Created Time",
  "Updated Time",
  "Drug Name",
  "Ingredient",
  "Strength",
  "Brand Name",
  "Source",
  "NDC",
  "Pkg",
  "Manufacturer",
  "Brand Id",
  "Is Deleted",
  "formTcl3",
  "Client Drug Flag",
  "Brand Type",
  "Status"
];
const BUSINESS_COMPUTATION_OPTIONS = [
  { value: 'primaryAddress', label: 'Primary Address' },
  { value: 'primarySpecialty', label: 'Primary Specialty' },
];
const PRIMARY_ADDRESS_BUSINESS_LABELS = new Set([
  'Address Line 1',
  'Address Line 2',
  'City',
  'State',
  'Zip',
]);
const BUSINESS_SUMMARY_FILLERS = [
  'Source',
  'First Name',
  'Middle Name',
  'Last Name',
  'Suffix',
  'Gender',
  'Phone Number',
  'Birth Year',
  'NPI ID',
  'ISN ID',
  'PMC ID',
  'City',
  'State',
  'Zip',
];
const WORKSPACE_COLUMN_WIDTHS = {
  compact: 120,
  standard: 180,
  action: 110,
};

const sectionCardSx = {
  minHeight: 300,
  p: 2.5,
  borderRadius: 3,
  border: '1px solid',
  borderColor: '#dfe3ea',
  boxShadow: 'none',
  backgroundColor: '#fff',
};

const actionButtonSx = {
  minWidth: 'auto',
  px: 2.5,
  py: 0.9,
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: 2,
  backgroundColor: '#2196f3',
  color: '#fff',
  '& .MuiButton-startIcon': {
    mr: 0.9,
    '& > *:nth-of-type(1)': {
      
    },
  },
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#1d88e5',
    boxShadow: 'none',
  },
};

const secondaryButtonSx = {
  minWidth: 'auto',
  px: 1.75,
  py: 0.7,
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: 2,
  color: '#374151',
  borderColor: '#d1d5db',
  backgroundColor: '#fff',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#f9fafb',
    borderColor: '#9ca3af',
    boxShadow: 'none',
  },
};

const modalTitleSx = {
  px: 3,
  py: 2.25,
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const primaryButtonSx = {
  minWidth: 'auto',
  px: 1.75,
  py: 0.7,
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: 2,
  backgroundColor: '#2196f3',
  color: '#fff',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#1d88e5',
    boxShadow: 'none',
  },
};

const fieldLabelSx = {
  ...standardFieldLabelSx,
};

const selectPlaceholderSx = {
  color: '#9ca3af',
};

const emptyStateCardSx = {
  px: 3,
  py: 3.5,
  border: '1px dashed #cbd5e1',
  borderRadius: 2,
  backgroundColor: '#f8fafc',
};

const backButtonSx = {
  color: '#111827',
  backgroundColor: '#f3f4f6',
  width: 36,
  height: 36,
  '&:hover': {
    backgroundColor: '#e5e7eb',
  },
};

const createBuilderRow = () => ({
  operator: '',
  comparator: '',
  value: '',
});

const createMatchRuleItem = () => ({
  label: '',
  name: '',
  matchAction: '',
  score: '',
  builderRows: [createBuilderRow()],
  rawRuleText: '',
});

const createBusinessRuleItem = (attribute = '') => ({
  computationType: getBusinessComputationValue(attribute),
  label: attribute,
  strategy: '',
  source: [],
  filterAttribute: '',
  filterValue: '',
  rank: '',
});

const createForm = () => ({
  groupName: '',
  groupDescription: '',
  defaultGroup: false,
  matchRules: [],
  businessRules: [],
});

const getCurrentTimestamp = () => new Date().toISOString();

const normalizeAttributeOptions = (rows) => rows.map((item) => item.displayName || item.name).filter(Boolean);
function getBusinessComputationLabel(value) {
  return BUSINESS_COMPUTATION_OPTIONS.find((item) => item.value === value)?.label || value || '';
}
function getBusinessComputationValue(label) {
  if (!label) {
    return '';
  }

  const normalized = label.toLowerCase();
  if (PRIMARY_ADDRESS_BUSINESS_LABELS.has(label) || normalized.includes('address')) {
    return 'primaryAddress';
  }
  if (normalized.includes('specialty')) {
    return 'primarySpecialty';
  }

  return label;
}
const formatBusinessLabel = (item) => item?.label || getBusinessComputationLabel(item?.computationType);
const formatBusinessStrategy = (item) => {
  if (item?.strategy && item.strategy !== 'SourceBased' && item.strategy !== 'Source Based') {
    return item.strategy;
  }

  if (formatBusinessLabel(item) === 'Source') {
    return 'Aggregation';
  }

  return 'Source Based';
};

const parseMatchRuleToBuilderRows = (rule) => {
  if (!rule) {
    return [createBuilderRow()];
  }

  if (Array.isArray(rule.operations) && rule.operations.length > 0) {
    return rule.operations.map((operation, index) => ({
      operator: index === 0 ? '' : rule.condition || '',
      comparator: operation.match || '',
      value: operation.field || '',
    }));
  }

  return [createBuilderRow()];
};

const buildRuleObjectFromRows = (rows, rawRuleText) => {
  const validRows = rows.filter((row) => row.comparator && row.value);
  if (validRows.length === 0) {
    if (!rawRuleText.trim()) {
      return null;
    }

    try {
      return JSON.parse(rawRuleText);
    } catch {
      return { expression: rawRuleText.trim() };
    }
  }

  return {
    condition: validRows[1]?.operator || 'and',
    operations: validRows.map((row) => ({
      match: row.comparator,
      field: row.value,
    })),
  };
};

const flattenBusinessRules = (computationTypes = {}) => Object.entries(computationTypes)
  .flatMap(([computationType, entries]) => (
    Array.isArray(entries)
      ? [...entries]
        .sort((left, right) => (Number(left?.rank) || 0) - (Number(right?.rank) || 0))
        .map((entry) => ({
          computationType,
          label: entry.label || computationType,
          strategy: entry.strategy === 'SourceBased' ? 'Source Based' : (entry.strategy || 'Source Based'),
          source: Array.isArray(entry.source)
            ? entry.source
            : entry.source
              ? String(entry.source).split(',').map((item) => item.trim()).filter(Boolean)
              : [],
          filterAttribute: entry.filterAttribute || '',
          filterValue: entry.filterValue || '',
          rank: entry.rank || '',
        }))
      : []
  ));

const createEmptyComputationTypes = (existingComputationTypes = {}) => ({
  ...BUSINESS_COMPUTATION_KEYS.reduce((accumulator, key) => ({
    ...accumulator,
    [key]: null,
  }), {}),
  ...Object.keys(existingComputationTypes).reduce((accumulator, key) => ({
    ...accumulator,
    [key]: null,
  }), {}),
});

const normalizeBusinessRuleEntry = (entry, rank) => ({
  strategy: entry.strategy === 'Source Based' ? 'SourceBased' : (entry.strategy || 'SourceBased'),
  source: Array.isArray(entry.source)
    ? entry.source
    : String(entry.source || '').split(',').map((item) => item.trim()).filter(Boolean),
  filterAttribute: entry.filterAttribute || null,
  filterValue: entry.filterValue || null,
  rank,
});

const normalizeBusinessRuleDraft = (entry = {}) => ({
  ...createBusinessRuleItem(entry.label || ''),
  ...entry,
  label: entry.label || getBusinessComputationLabel(entry.computationType) || '',
  source: Array.isArray(entry.source)
    ? entry.source
    : String(entry.source || '').split(',').map((item) => item.trim()).filter(Boolean),
  filterAttribute: entry.filterAttribute || '',
  filterValue: entry.filterValue || '',
});

const buildMatchPayload = (form, existingRow, currentUser = 'system') => {
  const now = getCurrentTimestamp();

  return {
    matchRuleGroups: [
      {
        ...existingRow,
        groupName: form.groupName,
        groupDescription: form.groupDescription,
        defaultGroup: form.defaultGroup,
        createdTime: existingRow?.createdTime || now,
        createdBy: existingRow?.createdBy || currentUser,
        updatedTime: now,
        updatedBy: currentUser,
        matchRules: form.matchRules
          .filter((rule) => rule.name || rule.matchAction)
          .map((rule) => ({
            name: rule.name,
            label: rule.label,
            matchAction: rule.matchAction,
            score: rule.score ? Number(rule.score) : undefined,
            createdTime: existingRow?.createdTime || now,
            createdBy: existingRow?.createdBy || currentUser,
            updatedTime: now,
            updatedBy: currentUser,
            rule: buildRuleObjectFromRows(rule.builderRows, rule.rawRuleText),
          })),
      },
    ],
  };
};

const buildBusinessPayload = (form, existingRow, currentUser = 'system') => {
  const now = getCurrentTimestamp();
  const groupedEntries = createEmptyComputationTypes(existingRow?.computationTypes);

  form.businessRules
    .filter((entry) => entry.computationType && entry.strategy)
    .forEach((entry) => {
      if (!Array.isArray(groupedEntries[entry.computationType])) {
        groupedEntries[entry.computationType] = [];
      }

      groupedEntries[entry.computationType].push(entry);
    });

  Object.keys(groupedEntries).forEach((computationType) => {
    if (!Array.isArray(groupedEntries[computationType]) || groupedEntries[computationType].length === 0) {
      groupedEntries[computationType] = null;
      return;
    }

    groupedEntries[computationType] = groupedEntries[computationType].map((entry, index) => (
      normalizeBusinessRuleEntry(entry, index + 1)
    ));
  });

  return {
    businessRuleGroups: [
      {
        ...existingRow,
        groupName: form.groupName,
        groupDescription: form.groupDescription,
        defaultGroup: form.defaultGroup,
        createdTime: existingRow?.createdTime || now,
        createdBy: existingRow?.createdBy || currentUser,
        updatedTime: now,
        updatedBy: currentUser,
        computationTypes: groupedEntries,
      },
    ],
  };
};

const ConfigWorkspacePage = ({ title, entityType, actionType }) => {
  const navigate = useNavigate();
  const { groupName: groupNameParam } = useParams();
  const location = useLocation();
  const { execute, loading, error } = useAPICall();
  const { user } = useAuth();
  const [localError, setLocalError] = useState('');
  const [form, setForm] = useState(createForm());
  const [selectedBusinessAttribute, setSelectedBusinessAttribute] = useState('');
  const [strategyDraft, setStrategyDraft] = useState(createBusinessRuleItem());
  const [strategyDialogOpen, setStrategyDialogOpen] = useState(false);
  const [editingBusinessRuleIndex, setEditingBusinessRuleIndex] = useState(null);
  const [editingMatchRuleIndex, setEditingMatchRuleIndex] = useState(null);

  const [businessRulesPagination, setBusinessRulesPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [businessSummaryPagination, setBusinessSummaryPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [matchRulesPagination, setMatchRulesPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const isMatchWorkspace = actionType.includes('match');
  const isBusinessWorkspace = actionType.includes('business');
  const existingRow = location.state?.row || null;
  const isEditMode = Boolean(existingRow);
  const [showBusinessRuleBuilder, setShowBusinessRuleBuilder] = useState(Boolean(existingRow) && isBusinessWorkspace);
  const isSystemGroup = useMemo(
    () => form.groupName?.toLowerCase() === 'system',
    [form.groupName]
  );
  const configBasePath = `/configuration/${entityType}`;
  const backPath = `${configBasePath}/${isBusinessWorkspace ? 'business-rules' : 'match-rules'}`;
  const attributeOptions = useMemo(
    () => (isBusinessWorkspace
      ? (entityType === 'drug' ? DRUG_BUSINESS_ATTRIBUTE_OPTIONS : HCP_BUSINESS_ATTRIBUTE_OPTIONS)
      : normalizeAttributeOptions(entityType === 'drug' ? DRUG_MODEL_SEED : HCP_MODEL_SEED)),
    [entityType, isBusinessWorkspace]
  );
  
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    target: null, // { type: 'matchRule' | 'businessRule' | 'businessRuleGroup', payload: any }
  });
  useEffect(() => {
    if (!existingRow) {
      setForm(createForm());
      setSelectedBusinessAttribute('');
      setShowBusinessRuleBuilder(false);
      return;
    }

    const businessRules = existingRow.computationTypes ? flattenBusinessRules(existingRow.computationTypes) : [];
    const groupName = existingRow.groupName || existingRow.group_name || existingRow.name || '';
    const isSystem = groupName.toLowerCase() === 'system';

    setForm({
      groupName,
      groupDescription: existingRow.groupDescription || existingRow.description || existingRow.group_description || '',
      defaultGroup: Boolean(existingRow.defaultGroup || existingRow.default_group || existingRow.default),
      matchRules: Array.isArray(existingRow.matchRules)
        ? existingRow.matchRules.map((rule) => ({
          label: rule.label || rule.ruleLabel || rule.name || '',
          name: rule.name || '',
          matchAction: rule.matchAction || rule.type || '',
          score: rule.score || '',
          builderRows: parseMatchRuleToBuilderRows(rule.rule),
          rawRuleText: rule.rule ? JSON.stringify(rule.rule, null, 2) : '',
          isSystemRule: isSystem,
        }))
        : [],
      businessRules: businessRules.map(br => ({ ...br, isSystemRule: isSystem })),
    });

    const initialAttribute = location.state?.selectedAttribute 
      || businessRules[0]?.label 
      || getBusinessComputationLabel(businessRules[0]?.computationType) 
      || '';
    setSelectedBusinessAttribute(initialAttribute);
    setShowBusinessRuleBuilder(isBusinessWorkspace);
  }, [existingRow, isBusinessWorkspace]);

  const visibleBusinessRules = useMemo(
    () => {
      const exactMatches = form.businessRules.filter((item) => formatBusinessLabel(item) === selectedBusinessAttribute);

      if (exactMatches.length > 0) {
        return exactMatches;
      }

      const mappedComputationType = getBusinessComputationValue(selectedBusinessAttribute);
      return form.businessRules.filter((item) => item.computationType === mappedComputationType);
    },
    [form.businessRules, selectedBusinessAttribute]
  );
  const hasSelectedBusinessAttribute = Boolean(selectedBusinessAttribute);

  const businessSummaryRows = useMemo(() => {
    const existingKeys = Object.entries(existingRow?.computationTypes || {})
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([key]) => key);

    const formKeys = form.businessRules
      .map((item) => item.computationType)
      .filter(Boolean);

    const validKeys = Array.from(new Set([...existingKeys, ...formKeys]));

    const baseRows = validKeys.map((key) => ({
      computationType: key,
      label: key,
      strategy: 'Source based',
      isActualKey: true,
    }));

    if (validKeys.length <= 1) {
      return baseRows;
    }

    const fillerRows = BUSINESS_SUMMARY_FILLERS
      .filter((label) => !validKeys.includes(label))
      .map((label) => ({
        computationType: getBusinessComputationValue(label),
        label,
        strategy: label === 'Source' ? 'Aggregation' : 'Source based',
        isActualKey: false,
      }));

    return [...baseRows, ...fillerRows];
  }, [form.businessRules]);

  const updateFormField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addMatchRule = () => {
    if (isEditMode) {
      setForm((prev) => {
        const nextMatchRules = [...prev.matchRules, createMatchRuleItem()];
        setEditingMatchRuleIndex(nextMatchRules.length - 1);
        return { ...prev, matchRules: nextMatchRules };
      });
      return;
    }

    setForm((prev) => ({ ...prev, matchRules: [...prev.matchRules, createMatchRuleItem()] }));
  };
  const confirmDelete = async () => {
    if (!deleteConfirm.target) return;

    if (deleteConfirm.target.type === 'matchRule') {
      const ruleIndex = deleteConfirm.target.payload;
      const nextRules = form.matchRules.filter((_, index) => index !== ruleIndex);

      setForm((prev) => ({
        ...prev,
        matchRules: nextRules,
      }));

      if (editingMatchRuleIndex === ruleIndex) {
        setEditingMatchRuleIndex(null);
      }

      if (isEditMode) {
        const currentUser = user?.username || user?.displayName || 'system';
        try {
          await execute(
            API_CONFIG.config.updateMatchRules,
            buildMatchPayload({ ...form, matchRules: nextRules }, existingRow, currentUser),
            {
              successMessage: `Rule deleted successfully`,
              showSuccessToast: true,
              errorMessage: `Unable to delete rule.`,
            }
          );
        } catch (err) {}
      }
    } else if (deleteConfirm.target.type === 'businessRule') {
      const target = deleteConfirm.target.payload;
      const nextRules = form.businessRules.filter((item) => item !== target);
      
      setForm((prev) => ({
        ...prev,
        businessRules: nextRules,
      }));

      if (isEditMode) {
        const currentUser = user?.username || user?.displayName || 'system';
        try {
          await execute(
            API_CONFIG.config.updateBusinessRules,
            buildBusinessPayload({ ...form, businessRules: nextRules }, existingRow, currentUser),
            {
              successMessage: `Rule deleted successfully`,
              showSuccessToast: true,
              errorMessage: `Unable to delete rule.`,
            }
          );
        } catch (err) {}
      }
    } else if (deleteConfirm.target.type === 'businessRuleGroup') {
      const target = deleteConfirm.target.payload;
      const nextRules = form.businessRules.filter(
        (item) => item.computationType !== target.computationType
      );

      setForm((prev) => ({
        ...prev,
        businessRules: nextRules,
      }));

      if (isEditMode) {
        const currentUser = user?.username || user?.displayName || 'system';
        try {
          await execute(
            API_CONFIG.config.updateBusinessRules,
            buildBusinessPayload({ ...form, businessRules: nextRules }, existingRow, currentUser),
            {
              successMessage: `Rule group deleted successfully`,
              showSuccessToast: true,
              errorMessage: `Unable to delete rule group.`,
            }
          );
        } catch (err) {}
      }
    }

    setDeleteConfirm({ open: false, target: null });
  };

  const removeMatchRule = (ruleIndex) => {
    setDeleteConfirm({ open: true, target: { type: 'matchRule', payload: ruleIndex } });
  };

  const openMatchRuleEditor = (ruleIndex) => {
    setEditingMatchRuleIndex(ruleIndex);
  };

  const updateMatchRuleField = (ruleIndex, field, value) => {
    setForm((prev) => ({
      ...prev,
      matchRules: prev.matchRules.map((rule, index) => (
        index === ruleIndex ? { ...rule, [field]: value } : rule
      )),
    }));
  };

  const updateBuilderRow = (ruleIndex, rowIndex, field, value) => {
    setForm((prev) => ({
      ...prev,
      matchRules: prev.matchRules.map((rule, index) => (
        index === ruleIndex
          ? {
            ...rule,
            builderRows: rule.builderRows.map((row, builderIndex) => (
              builderIndex === rowIndex ? { ...row, [field]: value } : row
            )),
          }
          : rule
      )),
    }));
  };

  const addBuilderRow = (ruleIndex) => {
    setForm((prev) => ({
      ...prev,
      matchRules: prev.matchRules.map((rule, index) => (
        index === ruleIndex
          ? { ...rule, builderRows: [...rule.builderRows, createBuilderRow()] }
          : rule
      )),
    }));
  };

  const removeBuilderRow = (ruleIndex, rowIndex) => {
    setForm((prev) => ({
      ...prev,
      matchRules: prev.matchRules.map((rule, index) => {
        if (index !== ruleIndex) {
          return rule;
        }

        const nextRows = rule.builderRows.filter((_, builderIndex) => builderIndex !== rowIndex);
        return { ...rule, builderRows: nextRows.length > 0 ? nextRows : [createBuilderRow()] };
      }),
    }));
  };

  const openStrategyDialog = (item = null, index = null) => {
    setEditingBusinessRuleIndex(index);
    setStrategyDraft(item
      ? normalizeBusinessRuleDraft({ ...item, label: formatBusinessLabel(item) })
      : createBusinessRuleItem(selectedBusinessAttribute));
    setStrategyDialogOpen(true);
  };

  const closeStrategyDialog = () => {
    setStrategyDialogOpen(false);
    setStrategyDraft(createBusinessRuleItem(selectedBusinessAttribute));
    setEditingBusinessRuleIndex(null);
  };

  const saveStrategy = () => {
    const rawDraft = {
      ...strategyDraft,
      label: strategyDraft.label || selectedBusinessAttribute,
      computationType: strategyDraft.computationType || getBusinessComputationValue(strategyDraft.label || selectedBusinessAttribute)
    };
    
    const normalizedDraft = normalizeBusinessRuleDraft(rawDraft);

    if (!normalizedDraft.computationType || !normalizedDraft.strategy) {
      setLocalError('Attribute and strategy are required.');
      return;
    }

    if ((normalizedDraft.strategy === 'Source Based' || normalizedDraft.strategy === 'SourceBased') && normalizedDraft.source.length === 0) {
      setLocalError('Source is required for Source Based strategy.');
      return;
    }

    setLocalError('');
    setForm((prev) => {
      let targetIndex = editingBusinessRuleIndex;
      
      if (targetIndex === null) {
        const existingIdx = prev.businessRules.findIndex(
          (item) => item.computationType === normalizedDraft.computationType
        );
        if (existingIdx !== -1) {
          targetIndex = existingIdx;
        }
      }

      if (targetIndex !== null) {
        return {
          ...prev,
          businessRules: prev.businessRules.map((item, index) => (
            index === targetIndex ? normalizedDraft : item
          )),
        };
      }

      return {
        ...prev,
        businessRules: [...prev.businessRules, normalizedDraft],
      };
    });
    closeStrategyDialog();
  };

  const removeBusinessRule = (target) => {
    setDeleteConfirm({ open: true, target: { type: 'businessRule', payload: target } });
  };

  const removeBusinessRuleGroup = (target) => {
    setDeleteConfirm({ open: true, target: { type: 'businessRuleGroup', payload: target } });
  };

  const getBusinessRuleIndexForRow = (target) => {
    const exactMatchIndex = form.businessRules.findIndex(
      (item) => formatBusinessLabel(item) === target.label
    );

    if (exactMatchIndex >= 0) {
      return exactMatchIndex;
    }

    return form.businessRules.findIndex(
      (item) => item.computationType === target.computationType
    );
  };

  const businessSummaryColumns = useMemo(() => ([
    {
      header: 'LABEL',
      accessorFn: (row) => formatBusinessLabel(row),
      id: 'label',
      size: WORKSPACE_COLUMN_WIDTHS.standard,
    },
    {
      header: 'STRATEGY',
      accessorFn: (row) => formatBusinessStrategy(row),
      id: 'strategy',
      size: WORKSPACE_COLUMN_WIDTHS.standard,
    },
    {
      header: 'ACTION',
      id: 'action',
      enableSorting: false,
      size: WORKSPACE_COLUMN_WIDTHS.action,
      Cell: ({ row }) => (
        <>
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              const label = formatBusinessLabel(row.original);
              navigate(`${configBasePath}/create-business-rules/${form.groupName || groupNameParam}`, { state: { row: existingRow, mode: 'edit', selectedAttribute: label } });
            }}
            disabled={row.original.isSystemRule}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => removeBusinessRuleGroup(row.original)}
            disabled={!row.original.isActualKey || row.original.isSystemRule}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
      muiTableHeadCellProps: { align: 'center' },
      muiTableBodyCellProps: { align: 'center' },
    },
  ]), [removeBusinessRuleGroup]);

  const businessRuleDetailColumns = useMemo(() => ([
    {
      header: 'STRATEGY',
      accessorFn: (row) => row.strategy || '-',
      id: 'strategy',
      size: WORKSPACE_COLUMN_WIDTHS.standard,
    },
    {
      header: 'SOURCE',
      accessorFn: (row) => (Array.isArray(row.source) && row.source.length > 0 ? row.source.join(', ') : '-'),
      id: 'source',
      size: WORKSPACE_COLUMN_WIDTHS.standard,
    },
    {
      header: 'ACTION',
      id: 'action',
      enableSorting: false,
      size: WORKSPACE_COLUMN_WIDTHS.action,
      Cell: ({ row }) => (
        <>
          <IconButton
            size="small"
            color="primary"
            onClick={() => openStrategyDialog(row.original, form.businessRules.indexOf(row.original))}
            disabled={row.original.isSystemRule}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => removeBusinessRule(row.original)} disabled={row.original.isSystemRule}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
      muiTableHeadCellProps: { align: 'center' },
      muiTableBodyCellProps: { align: 'center' },
    },
  ]), [removeBusinessRule]);

  const matchSummaryColumns = useMemo(() => ([
    {
      header: 'RULE LABEL',
      accessorFn: (row) => row.label || row.name || '-',
      id: 'ruleLabel',
      size: WORKSPACE_COLUMN_WIDTHS.standard,
    },
    {
      header: 'NAME',
      accessorFn: (row) => row.name || '-',
      id: 'name',
      size: WORKSPACE_COLUMN_WIDTHS.compact,
    },
    {
      header: 'TYPE',
      accessorFn: (row) => row.matchAction || '-',
      id: 'type',
      size: WORKSPACE_COLUMN_WIDTHS.compact,
    },
    {
      header: 'ACTION',
      id: 'action',
      enableSorting: false,
      size: WORKSPACE_COLUMN_WIDTHS.action,
      Cell: ({ row }) => (
        <>
          <IconButton size="small" color="primary" onClick={() => openMatchRuleEditor(form.matchRules.indexOf(row.original))} disabled={row.original.isSystemRule}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => removeMatchRule(form.matchRules.indexOf(row.original))} disabled={row.original.isSystemRule}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
      muiTableHeadCellProps: { align: 'center' },
      muiTableBodyCellProps: { align: 'center' },
    },
  ]), [form.matchRules, openMatchRuleEditor, removeMatchRule]);

  const hasNewRule = useMemo(() => {
    if (isMatchWorkspace) {
      return form.matchRules.some((rule) => !rule.isSystemRule);
    }
    return form.businessRules.some((rule) => !rule.isSystemRule);
  }, [form.matchRules, form.businessRules, isMatchWorkspace]);

  const onSave = async () => {
    if (!form.groupName.trim()) {
      setLocalError('Group Name is required.');
      return;
    }

    if (!form.groupDescription.trim()) {
      setLocalError('Group Description is required.');
      return;
    }

    if (isMatchWorkspace && form.matchRules.length === 0) {
      setLocalError('At least one rule is required.');
      return;
    }

    setLocalError('');

    const currentUser = user?.username || user?.displayName || 'system';

    try {
      await execute(
        isMatchWorkspace ? API_CONFIG.config.updateMatchRules : API_CONFIG.config.updateBusinessRules,
        isMatchWorkspace ? buildMatchPayload(form, existingRow, currentUser) : buildBusinessPayload(form, existingRow, currentUser),
        {
          successMessage: `${title} saved successfully`,
          showSuccessToast: true,
          errorMessage: `Unable to save ${title.toLowerCase()}.`,
        }
      );
      navigate(backPath);
    } catch {
    }
  };

  return (
    <Box sx={{ px: 1, pb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <PageBreadcrumbs items={[
        { label: isBusinessWorkspace ? 'Business Rules' : 'Match Rules', path: backPath },
        { label: isEditMode ? 'Edit' : 'Create' }
      ]} />
            </Box>
      <Fade in timeout={350}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            backgroundColor: '#fff',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #E5E7EB',
              borderLeft: '4px solid #1E88E5',
              display: 'flex',
              background: "#fbfdfe",
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 0.25, lineHeight: 1.2 }}>
                {isEditMode ? 'Edit Group Details' : 'Add Group Details'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                {isEditMode ? 'Update the details for this group.' : `Configure and save a new group.`}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ px: 3.5, pb: 4 }}>
        {isEditMode ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{  fontWeight: 600, color: '#111827' }}>
                Group Name:
              </Typography>
              <Typography sx={{  color: '#111827' }}>
                {form.groupName || '-'}
              </Typography>
            </Box>
            {!(editingMatchRuleIndex !== null &&
 editingMatchRuleIndex !== undefined)  && <Button className="action-btn process-btn action-btn process-btn"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                if (isMatchWorkspace) {
                  addMatchRule();
                } else if (actionType === 'business-group') {
                  navigate(`${configBasePath}/create-business-rules/${form.groupName || groupNameParam}`, { state: { row: existingRow, mode: 'edit' } });
                } else {
                  openStrategyDialog();
                }
              }}
              disabled={(isBusinessWorkspace && actionType !== 'business-group' && !selectedBusinessAttribute && !isEditMode)}
              sx={actionButtonSx}
            >
              {isMatchWorkspace ? 'Add Rule' : 'Rules'}
            </Button>}
          </Box>
        ) : null}

        {isEditMode && isSystemGroup && (
          <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
            This is a system-defined configuration group. Existing rules are protected and cannot be modified or removed, but you can add new rules to this group.
          </Alert>
        )}

        {!isEditMode ? (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{  fontWeight: 600, color: '#111827' }}>
              Group Details
            </Typography>
          </Box>
          {(!isBusinessWorkspace || isEditMode || !showBusinessRuleBuilder) ? (
            <Button className="action-btn process-btn action-btn process-btn"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={isMatchWorkspace ? addMatchRule : (isEditMode ? openStrategyDialog : () => setShowBusinessRuleBuilder(true))}
              disabled={(isBusinessWorkspace && isEditMode && !hasSelectedBusinessAttribute)}
              sx={actionButtonSx}
            >
              {isMatchWorkspace ? 'Add Rule' : 'Rules'}
            </Button>
          ) : null}
        </Box>
        ) : null}

        <Grid container spacing={4} sx={{ mb: 1 }}>
          <Grid item xs={12} md={6}>
            <Typography sx={fieldLabelSx}>
              Name <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Name"
              value={form.groupName}
              onChange={(event) => updateFormField('groupName', event.target.value)}
              size="small"
              disabled={isSystemGroup || isEditMode}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 38,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography sx={fieldLabelSx}>
              Description <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Description"
              value={form.groupDescription}
              onChange={(event) => updateFormField('groupDescription', event.target.value)}
              size="small"
              disabled={isSystemGroup || isEditMode}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 38,
                },
              }}
            />
          </Grid>
        </Grid>

        <FormControlLabel
          control={(
            <Checkbox
              checked={form.defaultGroup}
              onChange={(event) => updateFormField('defaultGroup', event.target.checked)}
              size="small"
            />
          )}
          disabled={isSystemGroup}
          label={(
            <Typography sx={{ color: '#111827' }}>
              {isMatchWorkspace ? 'Active' : 'Default'}{' '}
              <Box component="span" sx={{ color: '#6b7280' }}>
                {isMatchWorkspace
                  ? '(Active group will be used in the system)'
                  : '(Default group is used if group for specific roles are not configured)'}
              </Box>
            </Typography>
          )}
          />

        {isBusinessWorkspace ? (
          <>
            {(actionType === 'create-business-rules' || (!isEditMode && isBusinessWorkspace)) ? (
            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e5e7eb' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2}}>
                <Typography sx={{  fontWeight: 600, color: '#111827' }}>
                  Attribute Strategy
                </Typography>
                <Button className="action-btn process-btn action-btn process-btn"
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => openStrategyDialog()}
                  disabled={!hasSelectedBusinessAttribute}  
                  sx={actionButtonSx}
                >
                  Strategy
                </Button>
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Typography sx={fieldLabelSx}>Select Attribute</Typography>
                  <TextField
                    select
                    fullWidth
                    value={selectedBusinessAttribute}
                    onChange={(event) => setSelectedBusinessAttribute(event.target.value)}
                    size="small"
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (value) => (value
                        ? value
                        : <Box component="span" sx={selectPlaceholderSx}>Select Attribute</Box>),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "38px",
                      },
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        height: "38px",
                        boxSizing: "border-box",
                      },
                    }}
                  >
                    <MenuItem value="">Select Attribute</MenuItem>
                    {attributeOptions.map((item) => (
                      <MenuItem key={item} value={item}>{item}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              {hasSelectedBusinessAttribute ? (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{  fontWeight: 600, mb: 2, color: '#111827' }}>
                    Strategy
                  </Typography>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff' }}>
                    <GlobalMaterialTable
                      columns={businessRuleDetailColumns}
                      data={visibleBusinessRules}
                      emptyMessage={`No strategy added for ${selectedBusinessAttribute}.`}
                      enableTopToolbar={false}
                      enableGlobalFilter={false}
                      enableColumnFilters={false}
                      enableDensityToggle={false}
                      enableColumnActions={false}
                      enableHiding={false}
                      enableFullScreenToggle={false}
                      manualPagination={false}
                      page={businessRulesPagination.pageIndex + 1}
                      pageSize={businessRulesPagination.pageSize}
                      onPageChange={(p) => setBusinessRulesPagination(prev => ({ ...prev, pageIndex: p - 1 }))}
                      onPageSizeChange={(ps) => setBusinessRulesPagination(prev => ({ ...prev, pageSize: ps }))}
                      muiTablePaperProps={{ sx: { borderRadius: 0 } }}
                      muiTableHeadCellProps={{ sx: { backgroundColor: 'grey.50' } }}
                    />
                  </Box>
                </Box>
              ) : (
                <Box sx={emptyStateCardSx}>
                  <Typography sx={{  fontWeight: 600, color: '#0f172a', mb: 0.5 }}>
                    Select an attribute to enable strategy
                  </Typography>
                  <Typography sx={{  color: '#64748b' }}>
                    Choose an attribute above to view or modify its processing logic.
                  </Typography>
                </Box>
              )}
            </Box>
            ) : null}

            {(actionType === 'business-group' && isEditMode) ? (
            <Box sx={{ mt: 0 }}>
              {isEditMode ? (
                <GlobalMaterialTable
                  columns={businessSummaryColumns}
                  data={businessSummaryRows}
                  emptyMessage="No strategy added"
                  enableTopToolbar={false}
                  enableGlobalFilter={false}
                  enableColumnFilters={false}
                  enableDensityToggle={false}
                  enableColumnActions={false}
                  enableHiding={false}
                  enableFullScreenToggle={false}
                  manualPagination={false}
                  page={businessSummaryPagination.pageIndex + 1}
                  pageSize={businessSummaryPagination.pageSize}
                  onPageChange={(p) => setBusinessSummaryPagination(prev => ({ ...prev, pageIndex: p - 1 }))}
                  onPageSizeChange={(ps) => setBusinessSummaryPagination(prev => ({ ...prev, pageSize: ps }))}
                  muiTableBodyRowProps={({ row }) => ({
                    onDoubleClick: () => {
                      const label = row.original.label;
                      navigate(`${configBasePath}/create-business-rules/${form.groupName || groupNameParam}`, { state: { row: existingRow, mode: 'edit', selectedAttribute: label } });
                    },
                    sx: { cursor: 'pointer' },
                  })}
                />
              ) : hasSelectedBusinessAttribute ? (
                <Box sx={{ maxWidth: 860, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff' }}>
                  <GlobalMaterialTable
                    columns={businessRuleDetailColumns}
                    data={visibleBusinessRules}
                    emptyMessage={`No strategy added for ${selectedBusinessAttribute}.`}
                    enableTopToolbar={false}
                    enableGlobalFilter={false}
                    enableColumnFilters={false}
                    enableDensityToggle={false}
                    enableColumnActions={false}
                    enableHiding={false}
                    enableFullScreenToggle={false}
                    manualPagination={false}
                    page={businessRulesPagination.pageIndex + 1}
                    pageSize={businessRulesPagination.pageSize}
                    onPageChange={(p) => setBusinessRulesPagination(prev => ({ ...prev, pageIndex: p - 1 }))}
                    onPageSizeChange={(ps) => setBusinessRulesPagination(prev => ({ ...prev, pageSize: ps }))}
                    muiTablePaperProps={{ sx: { borderRadius: 0 } }}
                    muiTableHeadCellProps={{ sx: { backgroundColor: 'grey.50' } }}
                  />
                </Box>
              ) : (
                <Box sx={{ ...emptyStateCardSx, maxWidth: 860 }}>
                  <Typography sx={{  fontWeight: 600, color: '#0f172a', mb: 0.5 }}>
                    Select an attribute to enable strategy
                  </Typography>
                  <Typography sx={{  color: '#64748b' }}>
                    Choose an attribute above, then use the `Strategy` button to add rules for it.
                  </Typography>
                </Box>
              )}
            </Box>
            ) : null}
          </>
        ) : null}

        {isMatchWorkspace ? (
          <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #e5e7eb' }}>
            {isEditMode && editingMatchRuleIndex === null ? (
              <GlobalMaterialTable
                columns={matchSummaryColumns}
                data={form.matchRules}
                emptyMessage="No rules added"
                enableTopToolbar={false}
                enableGlobalFilter={false}
                enableColumnFilters={false}
                enableDensityToggle={false}
                enableColumnActions={false}
                enableHiding={false}
                enableFullScreenToggle={false}
                manualPagination={false}
                page={matchRulesPagination.pageIndex + 1}
                pageSize={matchRulesPagination.pageSize}
                onPageChange={(p) => setMatchRulesPagination(prev => ({ ...prev, pageIndex: p - 1 }))}
                onPageSizeChange={(ps) => setMatchRulesPagination(prev => ({ ...prev, pageSize: ps }))}
                muiTableBodyRowProps={({ row }) => ({
                  onDoubleClick: row.original.isSystemRule ? undefined : () => openMatchRuleEditor(form.matchRules.indexOf(row.original)),
                  sx: { cursor: row.original.isSystemRule ? 'default' : 'pointer' },
                })}
              />
            ) : form.matchRules.map((rule, ruleIndex) => (
              (!isEditMode || editingMatchRuleIndex === ruleIndex) ? (
              <Box key={`rule-${ruleIndex}`} sx={{ mb: 4 ,mt:0}}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isEditMode ? (
                      <IconButton size="small" onClick={() => setEditingMatchRuleIndex(null)} sx={{ color: '#111827' }}>
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                    <Typography sx={{  fontWeight: 600, color: '#111827' }}>
                      Rules
                    </Typography>
                  </Box>
                  <IconButton color="error" onClick={() => removeMatchRule(ruleIndex)} disabled={rule.isSystemRule}>
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Grid container spacing={2} sx={{ mb: 1.5 }}>
                  <Grid item xs={12} md={4}>
                    <Typography sx={fieldLabelSx}>Name <Box component="span" sx={{ color: '#d32f2f' }}>*</Box></Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter Rule Name"
                      value={rule.name}
                      onChange={(event) => updateMatchRuleField(ruleIndex, 'name', event.target.value)}
                      disabled={rule.isSystemRule}
                       sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 38,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={fieldLabelSx}>Match Action <Box component="span" sx={{ color: '#d32f2f' }}>*</Box></Typography>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      placeholder="Select Match Action"
                      value={rule.matchAction}
                      onChange={(event) => updateMatchRuleField(ruleIndex, 'matchAction', event.target.value)}
                      disabled={rule.isSystemRule}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (value) => (value
                          ? value
                          : <Box component="span" sx={selectPlaceholderSx}>Select Match Action</Box>),
                      }}
                       sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 38,
                        },
                      }}
                    >
                      <MenuItem value="">Select Match Action</MenuItem>
                      {MATCH_ACTIONS.map((item) => (
                        <MenuItem key={item} value={item}>{item}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={fieldLabelSx}>Match Score <Box component="span" sx={{ color: '#d32f2f' }}>*</Box></Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Enter Match Score"
                      value={rule.score}
                      onChange={(event) => updateMatchRuleField(ruleIndex, 'score', event.target.value)}
                      disabled={rule.isSystemRule}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 38,
                        },
                      }}
                    />
                  </Grid>
                </Grid>

            <Grid container>
              <Grid item xs={12} md={8} className='pe-1'>
                <Typography sx={{  fontWeight: 500, mb: 0.5, color: '#111827' }}>
                  Rules Builder
                </Typography>

                <Autocomplete
                  multiple
                  fullWidth
                  options={attributeOptions}
                  size="small"
                  disabled={rule.isSystemRule}
                  value={rule.builderRows.map((row) => row.value).filter(Boolean)}
                  onChange={(event, values) => {
                    const nextRows = values.length > 0
                      ? values.map((value, index) => ({
                        operator: index === 0 ? '' : 'and',
                        comparator: rule.builderRows[index]?.comparator || 'exact',
                        value,
                      }))
                      : [createBuilderRow()];

                    updateMatchRuleField(ruleIndex, 'builderRows', nextRows);
                  }}
                  renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
                    <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                  ))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search and select the attributes you want to include in your match rules"
                    />
                  )}
                  sx={{ mb: 2 ,}}
                />
                              </Grid>
            </Grid>
                {rule.builderRows.map((row, rowIndex) => (
                  <Grid container spacing={2} key={`builder-${ruleIndex}-${rowIndex}`} sx={{ mb: 1.5, alignItems: 'center' }}>
                    <Grid item xs>
                      <TextField
                        fullWidth
                        select
                        size="small"
                        placeholder="Select Operator"
                        onChange={(event) => updateBuilderRow(ruleIndex, rowIndex, 'operator', event.target.value)}
                        disabled={rowIndex === 0 || rule.isSystemRule}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (value) => (value
                            ? value
                            : <Box component="span" sx={selectPlaceholderSx}>Select Operator</Box>),
                        }}
                         sx={{
                          '& .MuiOutlinedInput-root': {
                            height: 38,
                          },
                        }}
                      >
                        <MenuItem value="">Select Operator</MenuItem>
                        {OPERATORS.map((item) => (
                          <MenuItem key={item} value={item}>{item}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs>
                      <TextField
                        fullWidth
                        select
                        size="small"
                        placeholder="Select Comparator"
                        value={row.comparator}
                        onChange={(event) => updateBuilderRow(ruleIndex, rowIndex, 'comparator', event.target.value)}
                        disabled={rule.isSystemRule}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (value) => (value
                            ? value
                            : <Box component="span" sx={selectPlaceholderSx}>Select Comparator</Box>),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: 38,
                          },
                        }}
                      >
                        <MenuItem value="">Select Comparator</MenuItem>
                        {COMPARATORS.map((item) => (
                          <MenuItem key={item} value={item}>{item}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs>
                      <TextField
                        fullWidth
                        select
                        size="small"
                        placeholder="Select Attribute"
                        value={row.value}
                        onChange={(event) => updateBuilderRow(ruleIndex, rowIndex, 'value', event.target.value)}
                        disabled={rule.isSystemRule}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (value) => (value
                            ? value
                            : <Box component="span" sx={selectPlaceholderSx}>Select Attribute</Box>),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: 38,
                          },
                        }}
                      >
                        <MenuItem value="">Select Attribute</MenuItem>
                        {attributeOptions.map((item) => (
                          <MenuItem key={item} value={item}>{item}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs="auto">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <IconButton onClick={() => removeBuilderRow(ruleIndex, rowIndex)} disabled={rule.builderRows.length === 1 || rule.isSystemRule}>
                          <RemoveIcon sx={{ color: '#fc5a5a' }} />
                        </IconButton>
                        <IconButton onClick={() => addBuilderRow(ruleIndex)} disabled={rule.isSystemRule}>
                          <AddIcon sx={{ color: '#6161f9' }} />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                ))}


              </Box>
              ) : null
            ))}
          </Box>
        ) : null}

        {(!isSystemGroup || hasNewRule) && <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 4 }}>
          <Button className="action-btn cancel-btn action-btn cancel-btn" variant="outlined" onClick={() => navigate(backPath)} sx={secondaryButtonSx}>
            Cancel
          </Button>
          <Button className="action-btn process-btn action-btn process-btn" variant="contained" onClick={onSave} disabled={loading} sx={primaryButtonSx}>
            {isEditMode ? 'Update' : 'Save'}
          </Button>
        </Box>}
        </Box>
      </Paper>
      </Fade>

      <Dialog
        open={strategyDialogOpen}
        onClose={closeStrategyDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 1.5,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={modalTitleSx}>
          <Typography sx={{  
              fontWeight: 600, 
              color: '#2b2f38',
              '& .MuiTypography-root':{
                px:0.5,py:0.5
            }
           }}>
            Add Strategy
          </Typography>
          <IconButton onClick={closeStrategyDialog} size="small" sx={{ color: '#6b7280' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: '20px !important', pb: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Typography sx={fieldLabelSx}>Select Strategy</Typography>
              <TextField
                fullWidth
                select
                size="small"
                value={strategyDraft.strategy}
                onChange={(event) => setStrategyDraft((prev) => ({
                  ...prev,
                  strategy: event.target.value,
                  source: event.target.value === 'Source Based' ? prev.source : [],
                }))}
                sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "38px",
                    },
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      height: "38px",
                      boxSizing: "border-box",
                    },
                  }}
                disabled={strategyDraft.isSystemRule}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => (value
                    ? value
                    : <Box component="span" sx={selectPlaceholderSx}>Select Strategy</Box>),
                }}
              >
                <MenuItem value="" disabled>Select Strategy</MenuItem>
                {STRATEGY_TYPES.map((item) => (
                  <MenuItem key={item} value={item}>{item}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {strategyDraft.strategy === 'Source Based' ? (
              <Grid item xs={12}>
                <Typography sx={fieldLabelSx}>Select Source</Typography>
                <Autocomplete
                  multiple
                  options={SOURCE_OPTIONS}
                  disableCloseOnSelect
                  size="small"
                  value={Array.isArray(strategyDraft.source) ? strategyDraft.source : []}
                  onChange={(event, value) => setStrategyDraft((prev) => ({ ...prev, source: value }))}
                  disabled={strategyDraft.isSystemRule}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        key={option}
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                        sx={{ borderRadius: 1 }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={!strategyDraft.source?.length ? 'Select Source' : ''}
                      SelectProps={{
                        displayEmpty: true,
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      minHeight: '40px',
                    }
                  }}
                />
              </Grid>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.25, borderTop: '1px solid #e5e7eb' }}>
          <Button className="action-btn cancel-btn action-btn cancel-btn" onClick={closeStrategyDialog} variant="outlined" sx={secondaryButtonSx}>Cancel</Button>
          <Button className="action-btn process-btn action-btn process-btn" variant="contained" onClick={saveStrategy} sx={primaryButtonSx} disabled={strategyDraft.isSystemRule}>
            {editingBusinessRuleIndex !== null ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, target: null })}
        onConfirm={confirmDelete}
        itemName={deleteConfirm.target?.type === 'businessRuleGroup' ? 'rule group' : 'rule'}
      />
    </Box>
  );
};

ConfigWorkspacePage.propTypes = {
  title: PropTypes.string.isRequired,
  entityType: PropTypes.oneOf(['hcp', 'drug']).isRequired,
  actionType: PropTypes.string.isRequired,
};

export default ConfigWorkspacePage;
